/**
 * 对后端返回的ai选项进行清洗，生成前端需要的选项，并根据selectModel更新选项信息
 */

import { useAtom } from 'jotai'
import { useEffect, useMemo, useRef, useState } from 'react'
import { GET_MODEL_TAGS } from '~/graphqls/gqls'
import {
  AiCreateArtworkModel,
  SuperType,
  getModelTags,
  getModelTagsVariables,
  getModelTags_pictureGraph_aiCreateArtworkOptionMenu_styleModelTagOptions,
} from '~/graphqls/types'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { to400Image } from '~/utils'
import { UserTrainedModalVersionMap, formatStyleDecorationItem } from '../utils'
import {
  modelCodeAtom,
  optionsAtom,
  resolutionKeyAtom,
  selectModelAtom,
} from './atom'
import { useType } from '../useType'
import { merge } from 'lodash'

/** 重组后端options数据，生成前端选项 */
export default function useOptions() {
  const [options] = useAtom(optionsAtom)
  const [selectModel] = useAtom(selectModelAtom)
  const [resolutionKey] = useAtom(resolutionKeyAtom)
  const [modelCode] = useAtom(modelCodeAtom)
  const { isMJ, isAD, isSD, isTransparent, isControlnet, isMask } = useType()

  /** end */
  const [allDecorationOptions, setAllDecorationOptions] = useState<
    getModelTags_pictureGraph_aiCreateArtworkOptionMenu_styleModelTagOptions[]
  >([])
  const [allDecorationOptionsLoading, setAllDecorationOptionsLoading] =
    useState(true)
  // 存放缓存数据
  const styleDecorationCache = useRef<any>({})

  /** 模型主题 */
  const modelThemeOptions = useMemo(() => {
    if (isTransparent) {
      return [
        ...(options?.layerDiffusionModelThemeOption?.map(item => ({
          ...item,
          name: item?.name || '',
          url: to400Image(item?.url || ''),
          isNew: item?.isNew,
          modelCode: item?.modelCode || 0,
          category: '通用模型',
          model: AiCreateArtworkModel.STABLE_DIFFUSION,
          defaultSelectStyleDecorationOption: formatStyleDecorationItem(
            item?.defaultSelectStyleDecorationOption
          ),
        })) || []),
      ]
    } else {
      let mjModelThemeOptions = options?.mjModelThemeOptions
      if (isControlnet) {
        mjModelThemeOptions = options?.mjPatternModelThemeOptions
      }
      return [
        ...(options?.sdModelThemeOptions?.map(item => ({
          ...item,
          name: item?.name || '',
          url: to400Image(item?.url || ''),
          isNew: item?.isNew,
          modelCode: item?.modelCode || 0,
          category: '通用模型',
          model: AiCreateArtworkModel.STABLE_DIFFUSION,
          defaultSelectStyleDecorationOption: formatStyleDecorationItem(
            item?.defaultSelectStyleDecorationOption
          ),
        })) || []),
        ...(options?.adModelThemeOptions?.map(item => ({
          ...item,
          name: item?.name || '',
          url: to400Image(item?.url || ''),
          isNew: item?.isNew,
          modelCode: item?.modelCode || 0,
          category: '漫画模型',
          model: AiCreateArtworkModel.ANIME_DIFFUSION,
          defaultSelectStyleDecorationOption: formatStyleDecorationItem(
            item?.defaultSelectStyleDecorationOption
          ),
        })) || []),
        ...(mjModelThemeOptions?.map(item => ({
          ...item,
          name: item?.name || '',
          url: to400Image(item?.url || ''),
          isNew: item?.isNew,
          modelCode: item?.modelCode || 0,
          category: 'MJ模型',
          model: isControlnet
            ? AiCreateArtworkModel.PRETEND_MJ
            : AiCreateArtworkModel.MIDJOURNEY,
          defaultSelectStyleDecorationOption: formatStyleDecorationItem(
            item?.defaultSelectStyleDecorationOption
          ),
        })) || []),
      ]
    }
  }, [options, isTransparent, isControlnet])
  const curModelThemeOptions = useMemo(() => {
    if (isTransparent) {
      // 透明图返回全部的模型
      return modelThemeOptions
    }
    return modelThemeOptions.filter(item => item.model === selectModel)
  }, [modelThemeOptions, selectModel, isTransparent])
  /** end */

  /** 当前选中的模型主题 */
  const curModelTheme = useMemo(() => {
    return curModelThemeOptions.find(item => item.modelCode === modelCode)
  }, [curModelThemeOptions, modelCode])

  /** 标签选择-包含模型标签和风格选择 */
  const getModelTagsAction = useImperativeQuery<
    getModelTags,
    getModelTagsVariables
  >(GET_MODEL_TAGS)

  const styleDecorationOptions = useMemo(() => {
    if (isAD) {
      return options?.adStyleDecorationOptions || []
    } else if (isSD) {
      return options?.sdStyleDecorationOptions || []
    } else if (isMJ && modelCode === 301) {
      return options?.mjStyleDecorationOptions || []
    } else if (isMJ && modelCode === 302) {
      return options?.njStyleDecorationOptions || []
    } else if (isMJ && modelCode === 303) {
      return options?.mjv6StyleDecorationOptions || []
    } else if (isMJ && modelCode === 304) {
      return options?.njv6StyleDecorationOptions || []
    } else if (isMJ && modelCode === 305) {
      return options?.mjv61StyleDecorationOptions || []
    } else if (isMJ && modelCode === 306) {
      return options?.mjv7StyleDecorationOptions || []
    } else {
      return []
    }
  }, [isAD, isSD, isMJ, modelCode])

  useEffect(() => {
    if (!modelCode) return

    setAllDecorationOptionsLoading(true)

    if (styleDecorationCache.current[modelCode]) {
      setAllDecorationOptions([
        ...styleDecorationCache.current[modelCode],
        ...styleDecorationOptions,
      ])
      setTimeout(() => {
        setAllDecorationOptionsLoading(false)
      }, 100)

      return
    }

    getModelTagsAction({
      modelCode,
    })
      .then(res => {
        setAllDecorationOptions([
          ...(res.data.pictureGraph?.aiCreateArtworkOptionMenu
            ?.styleModelTagOptions || []),
          ...styleDecorationOptions,
        ] as getModelTags_pictureGraph_aiCreateArtworkOptionMenu_styleModelTagOptions[])
        merge(styleDecorationCache.current, {
          [modelCode]:
            (res.data.pictureGraph?.aiCreateArtworkOptionMenu
              ?.styleModelTagOptions as getModelTags_pictureGraph_aiCreateArtworkOptionMenu_styleModelTagOptions[]) ||
            [],
        })
      })
      .catch(() => {
        setAllDecorationOptions([])
      })
      .finally(() => {
        setAllDecorationOptionsLoading(false)
      })
  }, [modelCode])
  /** end */

  /** 画面大小 */
  const [curResolution, curSizeOptions] = useMemo(() => {
    const curResolution: Record<
      string,
      {
        width: number
        length: number
        price: number
        membersPrice?: number | null
        superType: SuperType | null
        supportTidal: boolean | null | undefined
      }[]
    > = {}
    const curSizeOptions: {
      name: string
      desc: string
      url: string
      disabled: boolean
    }[] = []

    // console.log(options?.allSizeOptions, 'options?.allSizeOptions')
    // console.log('current_resolutionKey', resolutionKey)

    // 备注：因为神奇涂抹没有模型选择，需要写死用size_for_base512这一套尺寸
    const curModelCodeSizeOptions =
      options?.allSizeOptions?.find(
        item =>
          item?.resolutionKey === (isMask ? 'size_for_base512' : resolutionKey)
      )?.sizeSetOptions || options?.allSizeOptions?.[0]?.sizeSetOptions

    curModelCodeSizeOptions?.forEach(item => {
      if (!item) return
      curSizeOptions.push({
        name: item.name!,
        desc: item.desc!,
        url: item.url!,
        disabled: item.resolutionList?.length === 0,
      })
      curResolution[item.name!] = []
      item?.resolutionList?.forEach(resolutionItem => {
        curResolution[item.name!].push({
          width: resolutionItem!.width!,
          length: resolutionItem!.length!,
          price: resolutionItem!.price!,
          membersPrice: resolutionItem?.membersPrice,
          superType: resolutionItem?.superType || null,
          supportTidal: resolutionItem?.supportTidal,
        })
      })
    })

    return [curResolution, curSizeOptions]
  }, [options, resolutionKey, isMask])
  /** end */

  /** 模型版本 */
  const modelVersion = useMemo(() => {
    return (
      modelThemeOptions.find(item => item.modelCode === modelCode)
        ?.modelVersion || ''
    )
  }, [modelThemeOptions, modelCode, options])

  /** 角色同人 */
  const characterOptions = useMemo(() => {
    return (
      options?.characterOptions
        ?.filter(it =>
          it?.supportModelVersions?.includes(modelVersion as string)
        )
        ?.map(item => ({
          name: item?.name!,
          key: item?.key!,
          type: item?.type,
          category: item?.category,
          isNew: item?.isNew,
        })) || []
    )
  }, [options?.characterOptions, modelVersion])
  /** end */

  /** Lora模型 */
  const modelFusionOptions = useMemo(() => {
    if (isMJ) {
      return []
    }
    const userTrainedModelOptions: any[] =
      options?.userTrainedModelOptions
        ?.filter(
          subItem =>
            UserTrainedModalVersionMap[
              subItem.baseModelOption?.baseModelType!
            ] === modelVersion
        )
        .map(item => ({
          name: item?.name,
          key: `user_${item.key}`,
          weight: item.weight,
          layerNumber: item.layerNumber,
          category: '我的',
        })) || []
    const modelFusionOptions =
      options?.modelFusionOptions
        ?.filter(it =>
          it?.supportModelVersions?.includes(modelVersion as string)
        )
        ?.map(item => ({
          name: item?.name!,
          key: item?.key!,
          type: item?.type,
          category: item?.category,
          weight: item?.weight,
          isNew: item?.isNew,
        })) || []
    if (userTrainedModelOptions.length) {
      modelFusionOptions.unshift(...userTrainedModelOptions)
    }
    return modelFusionOptions
  }, [
    options?.modelFusionOptions,
    options?.userTrainedModelOptions,
    modelVersion,
    selectModel,
  ])
  /** end */

  /** Lora推荐模型 */
  const recommendedModelFusionOptions = useMemo(() => {
    return (
      options?.recommendedModelFusionOptions
        ?.filter(it =>
          it?.supportModelVersions?.includes(modelVersion as string)
        )
        ?.map(item => ({
          name: item?.name!,
          key: item?.key!,
          type: item?.type,
          category: item?.category,
          weight: item?.weight,
          isNew: item?.isNew,
        })) || []
    )
  }, [options?.recommendedModelFusionOptions, modelVersion])

  return {
    /** 模型主题 */
    modelThemeOptions,
    /** 当前大模型下的模型主题 */
    curModelThemeOptions,
    /** 标签选择 */
    allDecorationOptions,
    /** 标签选择loading */
    allDecorationOptionsLoading,
    /** 角色同人  */
    characterOptions,
    /** Lora模型 */
    modelFusionOptions,
    /** Lora推荐模型 */
    recommendedModelFusionOptions,
    /** 当前尺寸 */
    curSizeOptions,
    /** 当前分辨率 */
    curResolution,
    /** 全部用户自训练融合模型 */
    userTrainedModelAllOptions: options?.userTrainedModelOptions,
    /** 是否是sdxl */
    isSdxl: !!curModelTheme?.sdxl,
    /** 当前选中的模型主题 */
    curModelTheme,
    /** 模型主题是否是flux架构的 */
    isFlux: curModelTheme?.modelVersion === 'flux',
  }
}
